/**
 * Feature Flags Configuration (Client-Side)
 *
 * Centralized feature flag management for development-only features.
 * These flags are controlled via environment variables and determine
 * which features are available in each environment.
 *
 * Usage:
 *   import { featureFlags } from '../config/featureFlags';
 *   if (featureFlags.promptManagement) { ... }
 *
 * Environment Variables:
 *   REACT_APP_FEATURE_PROMPT_MANAGEMENT - Enable AI prompt management (dev only)
 * Platform Handling:
 *   - Web: Uses process.env (webpack DefinePlugin) - MUST use static property access
 *   - iOS/Android: Uses react-native-config (Config.X)
 *
 * IMPORTANT: Webpack's DefinePlugin requires STATIC property access like
 * `process.env.REACT_APP_X`. Dynamic access like `process.env[key]` won't work!
 */

import {
  REACT_APP_FEATURE_AGGREGATE_SAVE,
  REACT_APP_FEATURE_PROMPT_MANAGEMENT,
  REACT_APP_FEATURE_RICH_CONTENT_BENCHMARK,
} from '@env';
import { Platform } from 'react-native';
// Use react-native-dotenv (@env) for native platforms - more reliable than react-native-config

/**
 * Get the prompt management feature flag value
 * Uses static property access for webpack compatibility
 */
function getPromptManagementFlag(): boolean {
  if (Platform.OS === 'web') {
    // MUST use static access for webpack DefinePlugin to work
    return process.env.REACT_APP_FEATURE_PROMPT_MANAGEMENT === 'true';
  }
  // iOS/Android use react-native-dotenv (@env)
  return REACT_APP_FEATURE_PROMPT_MANAGEMENT === 'true';
}

/**
 * Harness gap fix (#1106) — a benchmark-enabled release entry. `__DEV__`
 * alone compiles the candidate lab out of every profile/release build,
 * which blocks the device performance measurements the spec calls for.
 * This flag lets a deliberately-built profiling artifact opt back in
 * without weakening the `__DEV__` gate for normal QA/prod builds.
 */
function getRichContentBenchmarkFlag(): boolean {
  if (Platform.OS === 'web') {
    return process.env.REACT_APP_FEATURE_RICH_CONTENT_BENCHMARK === 'true';
  }
  return REACT_APP_FEATURE_RICH_CONTENT_BENCHMARK === 'true';
}

/**
 * Aggregate Save (#1501 Option B)
 *
 * Routes a full Save through POST /api/requests/:id/save (one call, one
 * Version History entry) instead of the legacy per-section call sequence.
 * The client falls back to the legacy path automatically when the server
 * answers 404 AGGREGATE_SAVE_DISABLED, so the flags can flip independently.
 */
function getAggregateSaveFlag(): boolean {
  if (Platform.OS === 'web') {
    return process.env.REACT_APP_FEATURE_AGGREGATE_SAVE === 'true';
  }
  return REACT_APP_FEATURE_AGGREGATE_SAVE === 'true';
}

export const featureFlags = {
  /**
   * Prompt Management Feature
   *
   * Enables the AI prompt management UI and navigation.
   * This feature is intended for development environments only.
   *
   * Set REACT_APP_FEATURE_PROMPT_MANAGEMENT=true in .env.local/.env.dev
   * Do NOT set this in .env.qa or .env.prod
   */
  promptManagement: getPromptManagementFlag(),
  /**
   * Rich Content Candidate Benchmark (#1106)
   *
   * Keeps the isolated candidate evaluation lab reachable in a non-DEV
   * build so init-time/footprint measurements can be taken on a real
   * device build instead of only the Metro DEV bundle.
   *
   * Set REACT_APP_FEATURE_RICH_CONTENT_BENCHMARK=true only in a dedicated
   * profiling build config. Never set it in `.env.native.qa` / `.env.native.prod`.
   */
  richContentBenchmark: getRichContentBenchmarkFlag(),
  /**
   * Aggregate Save (#1501 Option B) — one POST /:id/save per user Save.
   * Set REACT_APP_FEATURE_AGGREGATE_SAVE=true together with the server's
   * FEATURE_AGGREGATE_SAVE.
   */
  aggregateSave: getAggregateSaveFlag(),
} as const;
